import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useModal } from '../../../contexts/ModalContext';

const serviceDetails = [
  {
    number: '01',
    tagline: 'Every line drawn with purpose.',
    title: 'Custom Residential Architecture',
    approach:
      'We begin each custom home project by deeply understanding how you and your family live — your daily rhythms, aesthetic sensibilities, and long-term goals. This informs every spatial decision, from site orientation to room proportions.',
    scope:
      'Full architectural design services from initial concept through construction documentation, including site analysis, programming, schematic design, design development, and permit-ready drawings.',
    benefits: [
      'Fully custom design tailored to your lifestyle and property',
      'Optimized natural light and spatial flow from the first sketch',
      'Material selections aligned with your vision and budget',
      'Code-compliant, construction-ready documentation',
    ],
    image:
      'url(https://public.readdy.ai/ai/img_res/8e12d2d6293dfa5512b85f4762f5d13c.jpg)',
  },
  {
    number: '02',
    tagline: 'Elevate what already exists.',
    title: 'Renovations & Additions',
    approach:
      'Renovation work requires a careful reading of existing conditions, structure, and character. We design interventions that feel native to a home — not added-on — while maximizing functionality and long-term value.',
    scope:
      'Addition design, interior reconfiguration, structural assessment coordination, material harmonization, permit drawings, and construction support throughout the building phase.',
    benefits: [
      'Design that enhances and respects existing architectural character',
      'Maximized square footage within practical constraints',
      'Improved natural light quality and spatial flow',
      'Significant increase in long-term property value',
    ],
    image:
      'url(https://readdy.ai/api/search-image?query=luxury%20home%20renovation%20interior%20open%20concept%20living%20kitchen%20transformation%20high%20ceilings%20natural%20wood%20flooring%20marble%20accents%20warm%20light%20flooding%20through%20expansive%20windows%20architectural%20excellence%20premium%20residential%20New%20York%20renovation%20award%20winning%20design%20editorial%20photography&width=1200&height=900&seq=sd-reno-elite-2&orientation=landscape)',
  },
  {
    number: '03',
    tagline: 'From site to structure — complete.',
    title: 'New Home Design',
    approach:
      'New home design starts with the land itself — its orientation, topography, views, and relationship to the surrounding context. We design from the outside in and the inside out simultaneously.',
    scope:
      'Site planning, architectural programming, schematic design, design development, material specification, and complete construction documentation for building permits and contractor bidding.',
    benefits: [
      'Site-responsive design that maximizes your property\'s potential',
      'Comprehensive documentation package for smooth permitting',
      'Design that holds its architectural value for generations',
      'Full coordination support with engineers and builders',
    ],
    image:
      'url(https://readdy.ai/api/search-image?query=brand%20new%20luxury%20residential%20home%20construction%20aerial%20view%20modern%20architectural%20masterpiece%20clean%20geometric%20forms%20natural%20stone%20wood%20materials%20large%20panoramic%20windows%20dramatic%20sky%20premium%20new%20construction%20Westchester%20New%20York%20residential%20architecture%20editorial%20quality%20photography&width=1200&height=900&seq=sd-new-elite-3&orientation=landscape)',
  },
  {
    number: '04',
    tagline: 'Space that serves how you live.',
    title: 'Interior Spatial Planning',
    approach:
      'Great interior layout is invisible when done well — you simply feel at home. We develop spatial plans that create intuitive flow, maximize light, and ensure every square foot works as it should.',
    scope:
      'Interior layout development, room proportioning, circulation planning, natural light studies, and spatial documentation coordinated with architectural drawings.',
    benefits: [
      'Layouts designed around how you actually use each space',
      'Maximized natural light at every room',
      'Intuitive flow from entry to living to private areas',
      'Spatial plans that coordinate seamlessly with architecture',
    ],
    image:
      'url(https://readdy.ai/api/search-image?query=luxury%20residential%20interior%20spatial%20design%20architecture%20soaring%20double%20height%20ceilings%20natural%20light%20flooding%20through%20floor%20to%20ceiling%20windows%20refined%20warm%20materials%20wood%20floors%20white%20walls%20architectural%20photography%20premium%20home%20elegant%20minimalist%20contemporary%20flowing%20spatial%20arrangement%20editorial%20quality&width=1200&height=900&seq=sd-interior-elite-4&orientation=landscape)',
  },
];

export default function ServicesDetailed() {
  const headerRef = useScrollReveal();

  return (
    <section className="bg-soft-white">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-8 lg:px-16 pt-36 pb-20">
        <div ref={headerRef} className="reveal-on-scroll">
          <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">Our Offerings</p>
          <h2 className="font-serif text-5xl lg:text-7xl font-light text-dark-ink leading-[1.0]">
            Services in Depth
          </h2>
        </div>
      </div>

      {/* Service detail rows */}
      {serviceDetails.map((svc, i) => (
        <ServiceDetailRow key={i} service={svc} reverse={i % 2 !== 0} light={i % 4 === 0 || i % 4 === 3} />
      ))}

      {/* Additional services */}
      <AdditionalServices />
    </section>
  );
}

function ServiceDetailRow({
  service,
  reverse,
  light,
}: {
  service: (typeof serviceDetails)[0];
  reverse: boolean;
  light: boolean;
}) {
  const textRef = useScrollReveal();
  const imgRef = useScrollReveal();
  const { openModal } = useModal();

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 min-h-[600px] ${light ? 'bg-soft-white' : 'bg-parchment'}`}>
      {/* Image */}
      <div
        ref={imgRef}
        className={`reveal-scale relative min-h-[380px] lg:min-h-0 overflow-hidden ${reverse ? 'lg:order-2' : ''}`}
      >
        <div
          className="absolute inset-0 bg-cover bg-top hover:scale-[1.03] transition-transform duration-1000 ease-out"
          style={{ backgroundImage: service.image }}
        />
        <div className="absolute inset-0 bg-dark-ink/15" />
        <div className="absolute top-6 right-6">
          <span className="font-sans text-[10px] tracking-[0.22em] uppercase text-white/50 border border-white/20 px-4 py-2">
            {service.number}
          </span>
        </div>
      </div>

      {/* Text */}
      <div
        ref={textRef}
        className={`reveal-on-scroll flex flex-col justify-center px-10 lg:px-16 xl:px-20 py-16 lg:py-20 ${reverse ? 'lg:order-1' : ''}`}
      >
        <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-3">{service.tagline}</p>
        <h3 className="font-serif text-4xl font-light text-dark-ink leading-[1.1] mb-6">{service.title}</h3>
        <p className="font-sans text-sm text-warm-gray leading-[1.85] mb-5">{service.approach}</p>
        <p className="font-sans text-[11px] text-warm-gray/70 leading-[1.8] mb-8 italic">{service.scope}</p>

        <ul className="space-y-3 mb-10">
          {service.benefits.map((b, bi) => (
            <li key={bi} className="flex items-start gap-3">
              <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                <i className="ri-check-line text-bronze text-xs" />
              </div>
              <span className="font-sans text-sm text-charcoal">{b}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={openModal}
          className="self-start font-sans text-[10px] tracking-[0.2em] uppercase px-8 py-3.5 border border-dark-ink text-dark-ink hover:bg-dark-ink hover:text-soft-white transition-colors duration-300 cursor-pointer whitespace-nowrap"
        >
          Request Consultation
        </button>
      </div>
    </div>
  );
}

function AdditionalServices() {
  const ref = useScrollReveal();
  const { openModal } = useModal();

  const extras = [
    {
      title: 'Architectural Consultation',
      desc: 'Expert review and guidance for existing plans, feasibility assessments, and pre-design project exploration.',
    },
    {
      title: 'Construction Documentation',
      desc: 'Comprehensive permit-ready drawing sets with full specifications — ready for contractor bidding and municipal approval.',
    },
    {
      title: 'Site Analysis & Planning',
      desc: 'In-depth assessment of your property — orientation, topography, zoning, and context — before design begins.',
    },
    {
      title: 'Design Development Support',
      desc: 'Ongoing architectural guidance and detailed design coordination through the construction phase.',
    },
  ];

  return (
    <div className="bg-dark-ink py-36 px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="reveal-on-scroll mb-16">
          <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">Additional</p>
          <h3 className="font-serif text-4xl lg:text-5xl font-light text-white">More Ways We Help</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-l border-t border-white/10">
          {extras.map((item, i) => (
            <ExtraCard key={i} title={item.title} desc={item.desc} delay={i + 1} onCta={openModal} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ExtraCard({
  title,
  desc,
  delay,
  onCta,
}: {
  title: string;
  desc: string;
  delay: number;
  onCta: () => void;
}) {
  const ref = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`reveal-on-scroll reveal-delay-${Math.min(delay, 6)} p-10 border-r border-b border-white/10 group hover:bg-white/[0.04] transition-colors duration-300 cursor-default`}
    >
      <h4 className="font-serif text-xl font-light text-white mb-3 group-hover:text-bronze/80 transition-colors duration-300">
        <a href="#services" className="cursor-default">{title}</a>
      </h4>
      <p className="font-sans text-sm text-white/40 leading-relaxed mb-6">{desc}</p>
      <button
        onClick={onCta}
        className="font-sans text-[10px] tracking-[0.18em] uppercase text-white/35 hover:text-white transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
      >
        <span>Inquire</span>
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-right-line text-xs" />
        </div>
      </button>
    </div>
  );
}
